/**
 * Choosing who takes an order.
 *
 * The riders are already ranked and already checked against this order's cash,
 * so the dispatcher is not doing subtraction under pressure. Anyone who cannot
 * take it stays on the list with the reason — a name that quietly vanishes is
 * a question the dispatcher then has to go and ask.
 */
import { formatPKR } from '@foodrun/shared';

import { cashOnDelivery, headroom } from './dispatch.js';
import { Button } from '../../components/ui/Button.jsx';

export function AssignSheet({ order, riders, onAssign, onCancel, busy, error }) {
  const owed = cashOnDelivery(order);
  const available = riders.filter((r) => r.ok);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Send this order"
      className="fixed inset-0 z-30 flex items-center justify-center bg-black/70 p-4"
    >
      <div className="w-full max-w-md rounded-2xl bg-surface p-5">
        <h2 className="font-semibold text-lg text-ink">Send {order.order_number}</h2>
        <p className="mt-0.5 text-sm text-ink-2">
          {order.customer_name || 'No name'}
          {owed > 0 ? ` · ${formatPKR(owed)} to collect` : ' · already paid'}
        </p>

        {riders.length === 0 ? (
          <p className="py-10 text-center text-sm text-ink-3">No riders set up yet.</p>
        ) : available.length === 0 ? (
          <p className="mt-4 rounded-lg bg-warn-bg px-3.5 py-2.5 text-sm text-warn">
            Nobody can take this right now. Every rider is either at their cash limit or not
            available — the cash ones need to hand money in first.
          </p>
        ) : null}

        <ul className="mt-4 max-h-72 space-y-2 overflow-y-auto">
          {riders.map(({ rider, ok, reason }) => (
            <li key={rider.id}>
              <button
                onClick={() => onAssign(rider.id)}
                disabled={!ok || busy}
                className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors ${
                  ok
                    ? 'border-rule hover:border-brand-400 hover:bg-brand-400/10'
                    : 'cursor-not-allowed border-transparent bg-surface-3 opacity-60'
                }`}
              >
                <span className="min-w-0 flex-1">
                  <span className="block font-medium text-ink">{rider.name}</span>
                  <span className="block text-xs text-ink-2">
                    {ok
                      ? `Holding ${formatPKR(rider.cod_balance)} · room for ${formatPKR(headroom(rider))}`
                      : reason}
                  </span>
                </span>

                {rider.orders_out > 0 && (
                  <span className="shrink-0 text-xs tabular-nums text-ink-3">
                    {rider.orders_out} out
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>

        {error && (
          <p role="alert" className="mt-4 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">
            {error.message}
          </p>
        )}

        <Button variant="secondary" size="lg" className="mt-5 w-full" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
